import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import SearchBox from "@/components/feature/SearchBox";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/news", label: "新闻" },
  { to: "/reviews", label: "评测" },
  { to: "/industry", label: "行业动态" },
  { to: "/about", label: "关于我们" },
  { to: "/contact", label: "联系我们" },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setMobileSearch(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = menuOpen || mobileSearch ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen, mobileSearch]);

  const today = new Date();
  const dateText = `${today.getFullYear()}年${today.getMonth() + 1}月${today.getDate()}日`;

  return (
    <header className="sticky top-0 z-40">
      {/* utility bar */}
      <div className="hidden md:block bg-primary-950 text-background-200">
        <div className="max-w-[1280px] mx-auto px-6 h-9 flex items-center justify-between text-xs">
          <span className="flex items-center gap-2">
            <i className="ri-newspaper-line text-accent-400" />
            汽车资讯网 · 权威、专业、及时的汽车信息门户
          </span>
          <span className="flex items-center gap-4">
            <span>{dateText}</span>
            <span className="text-background-400">|</span>
            <span className="text-accent-400">演示数据平台</span>
          </span>
        </div>
      </div>

      {/* main nav */}
      <div className="bg-background-50 border-b border-background-200">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 md:gap-8 min-w-0">
            <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
              <span className="w-10 h-10 rounded-lg bg-primary-600 flex items-center justify-center">
                <i className="ri-car-fill text-background-50 text-xl" />
              </span>
              <span className="hidden sm:block leading-tight">
                <span className="block font-heading font-bold text-lg text-primary-950 tracking-wide">
                  汽车资讯网
                </span>
                <span className="block text-[11px] text-foreground-500">
                  AUTO NEWS PORTAL
                </span>
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                      isActive
                        ? "text-primary-700 font-semibold bg-primary-100"
                        : "text-foreground-700 hover:text-primary-700 hover:bg-background-100"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden md:block w-64 lg:w-72">
              <SearchBox />
            </div>
            <button
              type="button"
              aria-label="搜索"
              onClick={() => setMobileSearch(true)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-search-line text-xl" />
            </button>
            <button
              type="button"
              aria-label="菜单"
              onClick={() => setMenuOpen(true)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-menu-3-line text-2xl" />
            </button>
          </div>
        </div>
      </div>

      {/* mobile menu overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute right-0 top-0 h-full w-[78%] max-w-xs bg-background-50 p-5 flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <span className="font-heading font-bold text-lg text-primary-950">
                导航菜单
              </span>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setMenuOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>
            <div className="mb-5">
              <SearchBox />
            </div>
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-md text-sm transition-colors cursor-pointer ${
                      isActive
                        ? "bg-primary-100 text-primary-700 font-semibold"
                        : "text-foreground-800 hover:bg-background-100"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="mt-auto pt-6 text-xs text-foreground-500">
              汽车资讯网 · 演示数据平台
            </div>
          </div>
        </div>
      )}

      {/* mobile fullscreen search */}
      {mobileSearch && (
        <div className="fixed inset-0 z-50 md:hidden bg-background-50 p-4">
          <div className="flex items-center gap-3 mb-4">
            <button
              type="button"
              aria-label="返回"
              onClick={() => setMobileSearch(false)}
              className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-2xl" />
            </button>
            <span className="font-heading font-bold text-primary-950">全站搜索</span>
          </div>
          <SearchBox autoFocus size="lg" />
          <p className="mt-4 text-xs text-foreground-500">
            提示：搜索功能基于演示数据，支持标题、摘要与标签匹配。
          </p>
        </div>
      )}
    </header>
  );
}